'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Search, X, Menu, ChevronRight, Bell, LogOut, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';

const navLinks = [
  { label: 'News', href: '/#news' },
  { label: 'Suppliers', href: '/suppliers-directory' },
  { label: 'Market Data', href: '/market-data-page' },
  { label: 'FDA Tracker', href: '/fda-guidance-tracker-page' },
  { label: 'Advertise', href: '/advertise-page' },
];

interface Suggestion {
  id: string;
  slug: string;
  title: string;
  category: string;
}

export default function Header() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [accountOpen, setAccountOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const accountRef = useRef<HTMLDivElement>(null);
  const { user, signOut } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);

  const fetchSuggestions = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('articles')
        .select('id, slug, title, category')
        .or(`title.ilike.%${q}%,category.ilike.%${q}%`)
        .order('published_at', { ascending: false })
        .limit(5);
      setSuggestions(data || []);
    } catch {
      setSuggestions([]);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSuggestions(searchQuery);
    }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchSuggestions]);

  // Close account dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchOpen(false);
      setSuggestions([]);
      router.push(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  }

  async function handleSignOut() {
    await signOut();
    setAccountOpen(false);
    router.push('/');
    router.refresh();
  }

  return (
    <>
      <header className="sticky top-0 z-50 bg-white border-b border-border shadow-sm">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2 flex-shrink-0">
              <AppLogo size={32} />
              <span className="font-extrabold text-foreground text-lg tracking-tight leading-none">
                USA PEPTIDE PROS
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={`nav-${link.label}`}
                  href={link.href}
                  className="px-3 py-2 text-sm font-600 text-foreground hover:text-primary transition-colors duration-150 rounded-md hover:bg-muted"
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-2">
              {/* Search */}
              <button
                onClick={() => setSearchOpen(true)}
                aria-label="Open search"
                className="p-2 rounded-md hover:bg-muted transition-colors duration-150 text-foreground"
              >
                <Search size={20} />
              </button>

              {/* Auth: signed in → account dropdown, signed out → Sign In link */}
              {user ? (
                <div className="relative" ref={accountRef}>
                  <button
                    onClick={() => setAccountOpen(!accountOpen)}
                    className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm font-600 border border-border rounded-md hover:border-primary hover:text-primary transition-colors duration-150"
                  >
                    <User size={15} />
                    Account
                  </button>
                  {accountOpen && (
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-border rounded-lg shadow-lg py-1 z-50">
                      <div className="px-3 py-2 border-b border-border">
                        <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                      </div>
                      <Link
                        href="/alerts"
                        onClick={() => setAccountOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                      >
                        <Bell size={14} /> Manage Alerts
                      </Link>
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors text-left"
                      >
                        <LogOut size={14} /> Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  href="/sign-in"
                  className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 text-sm font-600 border border-border rounded-md hover:border-primary hover:text-primary transition-colors duration-150"
                >
                  Sign In
                </Link>
              )}

              {/* Mobile hamburger */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Toggle menu"
                className="lg:hidden p-2 rounded-md hover:bg-muted transition-colors duration-150"
              >
                {menuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile nav drawer */}
        {menuOpen && (
          <div className="lg:hidden border-t border-border bg-white px-4 py-3">
            {navLinks.map((link) => (
              <Link
                key={`mobile-nav-${link.label}`}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-between py-2.5 text-sm font-600 text-foreground border-b border-border last:border-0"
              >
                {link.label}
                <ChevronRight size={16} className="text-muted-foreground" />
              </Link>
            ))}
            {user ? (
              <>
                <Link
                  href="/alerts"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center justify-between py-2.5 text-sm font-600 text-foreground border-b border-border"
                >
                  Manage Alerts
                  <Bell size={16} className="text-muted-foreground" />
                </Link>
                <button
                  onClick={() => { handleSignOut(); setMenuOpen(false); }}
                  className="mt-3 w-full block text-center py-2 text-sm font-600 border border-border rounded-md text-foreground"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <Link
                href="/sign-in"
                onClick={() => setMenuOpen(false)}
                className="mt-3 block text-center py-2 text-sm font-700 bg-primary text-white rounded-md"
              >
                Sign In
              </Link>
            )}
          </div>
        )}
      </header>

      {/* Search overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setSearchOpen(false)}>
          <div
            className="bg-white border-b border-border shadow-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-w-screen-2xl mx-auto px-4 lg:px-8">
              <form onSubmit={handleSearchSubmit} className="flex items-center gap-3 h-16">
                <Search size={20} className="text-muted-foreground flex-shrink-0" />
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Search articles, categories, topics..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 text-base bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
                />
                <button
                  type="button"
                  onClick={() => {
                    setSearchOpen(false);
                    setSearchQuery('');
                    setSuggestions([]);
                  }}
                  className="p-2 rounded-md hover:bg-muted transition-colors"
                  aria-label="Close search"
                >
                  <X size={20} />
                </button>
              </form>

              {/* Typeahead suggestions */}
              {suggestions.length > 0 && (
                <div className="border-t border-border pb-2">
                  {suggestions.map((article) => (
                    <Link
                      key={`suggestion-${article.id}`}
                      href={`/article-detail-page?slug=${article.slug}`}
                      onClick={() => {
                        setSearchOpen(false);
                        setSearchQuery('');
                        setSuggestions([]);
                      }}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-muted transition-colors"
                    >
                      <Search size={14} className="text-muted-foreground flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-600 text-foreground truncate">{article.title}</p>
                        <p className="text-xs text-muted-foreground">{article.category}</p>
                      </div>
                    </Link>
                  ))}
                  <div className="px-4 py-2 border-t border-border">
                    <button
                      type="submit"
                      form="search-form"
                      onClick={handleSearchSubmit as unknown as React.MouseEventHandler}
                      className="text-xs font-600 text-primary hover:underline"
                    >
                      See all results for &ldquo;{searchQuery}&rdquo; →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}